import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FDBox,
  FDSearchInput,
  FDStack,
  FDTitleText,
} from '@facet-wealth/design';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { dataAttrs } from 'Helpers/Testing';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import LeversSidebarAdvancedOptionsPopoverButton from './leversSidebarAdvancedOptionsPopoverButton';
import { hasMatchingContentInSubgroups, matchesSearchTerm } from './leverSubAccordionLayout';
import LeverAccordionLayout from './leverAccordionLayout';
import { metaOverrideSet } from './metaOverrideSet';

const useStyles = buildUseStyles('LeversSidebarLayout', (theme) => ({
  sidebar: {
    padding: theme.spacing(0, 4),
  },
  sidebarSearchBar: {
    // select searchbar across states
    '& .FD-MuiInputBase-root, & .FD-MuiInputBase-root:hover, & .FD-MuiInputBase-root:focus': {
      backgroundColor: theme.palette.common.white,
      borderColor: theme.palette.facet.oceana500,
    },
    '& input': {
      color: theme.palette.facet.oceana500,
      fontWeight: theme.typography.fontWeightRegular,
      fontSize: theme.typography.subtitle2.fontSize,
    },
    '&& .FD-MuiInputAdornment-root .FD-MuiSvgIcon-root': {
      color: theme.palette.facet.oceana500,
    },
  },
  sidebarAdvancedOptions: {
    paddingTop: theme.spacing(2),
  },
}));

// Helper function to check if a lever is pinned or active
const isLeverPinnedOrActive = (lever) => {
  return !!lever?.isTruthy?.('isPinned') || !!lever?.isTruthy?.('isActive');
};

// Recursive function to collect levers with their parent labels as a wrapper object
const collectLeversWithParentLabels = (group, parentLabel, parentChain = []) => {
  const leversWithLabels = [];
  if (!group || !group.attr) return leversWithLabels;

  const currentLabel = group.attr('label') || parentLabel;
  const currentId = group.id?.() || group.id; // support either function or prop, depending on resource impl

  // Build chain including this current group so we can expand all ancestors later
  const currentChain = [...parentChain, { id: currentId, label: currentLabel }];

  // Collect levers from direct relationship, creating a wrapper object
  const directLevers = group.attr('levers')?.resources?.()?.list() || [];
  leversWithLabels.push(...directLevers.map(lever => ({
    lever,
    parentLabel: currentLabel,
    parentId: currentId,
    ancestorIds: currentChain.map(c => c.id),
  })));

  // Collect levers from nested leverSets, creating a wrapper object
  const leverSets = group.attr('leverSets')?.resources?.()?.list() || [];
  for (const setRes of leverSets) {
    const setLevers = setRes.attr('levers')?.resources?.()?.list() || [];
    leversWithLabels.push(...setLevers.map(lever => ({
      lever,
      parentLabel: currentLabel,
      parentId: currentId,
      ancestorIds: currentChain.map(c => c.id),
    })));
  }

  // Recursively collect levers from nested groups
  const nestedGroups = group.attr('groups')?.resources?.()?.list() || [];
  for (const nestedGroup of nestedGroups) {
    leversWithLabels.push(...collectLeversWithParentLabels(nestedGroup, currentLabel, currentChain));
  }

  return leversWithLabels;
};

const LeversSidebarLayout = ({ setSaving }) => {
  const [expandedIDs, setExpandedIDs] = useState(new Set());
  const [optimisticPinnedIds, setOptimisticPinnedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { resource, handleChange } = useMetaLayoutContext();
  const classes = useStyles();
  const resourceId = resource?.id?.();

  const handleChangeSearch = React.useCallback((value) => {
    setSearchTerm(value);
  }, [setSearchTerm]);

  const onLeverUpdate = React.useCallback((updatedLeverGroups) => {
    handleChange('groups', updatedLeverGroups.attr('groups'));
  }, [resource, handleChange]);

  useEffect(() => {
    if (!resource || !resource.attr || !resource.attr('groups')) return;

    const newExpandedIDs = new Set();
    const groupList = resource.attr('groups').resources().list();

    const allPinnedLevers = [];
    for (const group of groupList) {
      const groupLabel = group.attr('label');
      const allLevers = collectLeversWithParentLabels(group, groupLabel);
      allPinnedLevers.push(...allLevers.filter(item => isLeverPinnedOrActive(item.lever)));
    }

    if (allPinnedLevers.length > 0) {
      newExpandedIDs.add('__pinned__');
      const pinnedByGroupMap = allPinnedLevers.reduce((acc, item) => {
        const { parentLabel } = item;
        if (!acc[parentLabel]) {
          acc[parentLabel] = { label: parentLabel, levers: [] };
        }
        acc[parentLabel].levers.push(item.lever);
        return acc;
      }, {});

      Object.keys(pinnedByGroupMap).forEach(label => {
        newExpandedIDs.add(`pinned-${label}`);
      });
    }
    setExpandedIDs(newExpandedIDs);
  }, [resourceId]);

  const hasGroups = !!resource && !!resource.attr('groups');
  if (!hasGroups) {
    return null;
  }

  const groupList = resource.attr('groups').resources().list();
  const allLeversWithLabels = groupList.flatMap(group => collectLeversWithParentLabels(group, group.attr('label')));

  // Build a quick lookup from lever ID to parent label for optimistic expansion
  const parentLabelByLeverId = new Map(
    allLeversWithLabels.map(({ lever, parentLabel }) => [lever.id(), parentLabel]),
  );

  const onTogglePinOptimistic = (leverRes, isPinning) => {
    // Update the optimistic set
    setOptimisticPinnedIds((prev) => {
      const next = new Set(prev);
      const id = leverRes.id();
      if (isPinning) next.add(id); else next.delete(id);
      return next;
    });

    // Ensure pinned sections are expanded when pinning
    if (isPinning) {
      setExpandedIDs((prev) => {
        const next = new Set(prev);
        next.add('__pinned__');
        const parentLabel = parentLabelByLeverId.get(leverRes.id());
        if (parentLabel) next.add(`pinned-${parentLabel}`);
        return next;
      });
    }
  };

  // Filter pinned levers based on search term, include optimistic pins
  const filteredPinnedLevers = allLeversWithLabels
    .filter(item => (isLeverPinnedOrActive(item.lever) || optimisticPinnedIds.has(item.lever.id()))
            && (matchesSearchTerm(item.lever, searchTerm)
                || (item.parentLabel && item.parentLabel.toLowerCase().includes(searchTerm.toLowerCase()))));

  const pinnedByGroupMap = filteredPinnedLevers.reduce((acc, item) => {
    const {
      parentLabel, lever, parentId, ancestorIds,
    } = item;
    const key = parentId || parentLabel;
    if (!acc[key]) {
      acc[key] = {
        id: parentId, label: parentLabel, levers: [], ancestorIdsToExpand: ancestorIds || (parentId ? [parentId] : []),
      };
    }
    acc[key].levers.push(lever);
    return acc;
  }, {});

  const pinnedByGroup = Object.values(pinnedByGroupMap);
  const pinnedCount = filteredPinnedLevers.length;

  // Check for any matching content in groups to determine if the "No levers found" message should be displayed
  const hasVisibleGroupContent = groupList.some(group => {
    const groupTitleMatches = group.attr('label')?.toLowerCase().includes(searchTerm.toLowerCase());
    const hasMatchInSubgroups = hasMatchingContentInSubgroups(group, searchTerm);
    return groupTitleMatches || hasMatchInSubgroups;
  });

  const hasVisibleContent = pinnedCount > 0 || hasVisibleGroupContent;

  return (
    <div className={classes.sidebar} {...dataAttrs('levers-sidebar')}>
      <FDStack direction={'column'} flex={1} justifyContent={'space-between'}>
        <FDBox flex={1} {...dataAttrs('levers-search-bar-and-accordions-container')}>
          <FDBox flex={1} width={'100%'} alignSelf={'center'} {...dataAttrs('levers-search-bar')}>
            <div className={classes.sidebarSearchBar}>
              <FDSearchInput
                placeholder={'Search by Keyword'}
                onChange={(e) => handleChangeSearch(e.target.value)}
                value={searchTerm}
                fullWidth
                {...dataAttrs('levers-search-bar-input')}
              />
            </div>
          </FDBox>
          <FDStack direction={'column'} {...dataAttrs('levers-accordions')}>
            <MetaResourceRenderer
              resource={resource}
              layout={LeverAccordionLayout}
              metaOverrideSet={metaOverrideSet}
              LayoutProps={{
                expandedIDs,
                setExpandedIDs,
                loading,
                setLoading,
                setSaving,
                pinnedByGroup,
                pinnedCount,
                groupList,
                searchTerm,
                onTogglePinOptimistic,
                onLeverUpdate,
              }}
            />
            {/* Display placeholder text if all sections have been filtered out */}
            {!hasVisibleContent && (
            <FDStack alignSelf={'center'} justifyContent={'center'} paddingY={4}>
              <FDTitleText value={'No levers found for keyword.'} color={'textSecondary'} size={'lg'} />
            </FDStack>
            )}
          </FDStack>
        </FDBox>
        <FDStack direction={'column'} width={'100%'} alignSelf={'center'} justifyContent={'center'} className={classes.sidebarAdvancedOptions} {...dataAttrs('levers-advanced-options')}>
          <LeversSidebarAdvancedOptionsPopoverButton resource={resource} setLoading={setLoading} setSaving={setSaving} />
        </FDStack>
      </FDStack>
    </div>
  );
};

LeversSidebarLayout.propTypes = {
  setSaving: PropTypes.func,
};

export default LeversSidebarLayout;
